import React, {useId, useState} from 'react';
import {withRouter} from 'react-router-dom';
import {bindActionCreators} from 'redux';
import {connect} from 'react-redux';
import styled from 'styled-components';
import {Card} from '@betareader/design-system';

import Icon from '../../../components/AppIcon';
import {push} from '../../../navigation';

const UploadArea = styled(Card)`
  display: grid;
  justify-items: center;
  gap: 8px;
  padding: 20px;
  border: 2px dashed var(--br-border);
  text-align: center;
  input { max-width: 100%; }
`;

const acceptsImage = file =>
  ['image/png', 'image/jpeg', 'image/jpg'].includes(file.type) ||
  /\.(png|jpe?g)$/i.test(file.name);

export const CoverFileUpload = ({
  idToken,
  currentBook,
  uploadMessage,
  onComplete,
  onError,
  onClose
}) => {
  const inputId = useId();
  const [uploading, setUploading] = useState(false);

  const uploadFile = async file => {
    if (!file || uploading) return;
    if (!acceptsImage(file)) {
      onError?.({error: 'UnsupportedFileType', message: 'Choose a PNG or JPEG image.'});
      return;
    }
    setUploading(true);
    try {
      const body = new FormData();
      body.append('file', file);
      const response = await fetch(
        `${process.env.REACT_APP_API_HOST}${process.env.REACT_APP_API_PATH}/books/${currentBook._id}/cover-art`,
        {method: 'POST', headers: {'br-token': idToken}, body}
      );
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        onError?.({
          error: payload.error || payload.err || 'UploadError',
          message: payload.message || payload.msg || 'Upload failed'
        });
        return;
      }
      onComplete?.(payload, null);
    } catch (error) {
      onError?.({error: 'UploadError', message: error.message || 'Upload failed'});
    } finally {
      setUploading(false);
    }
  };

  return (
    <div>
      {onClose && (
        <Icon name="close" aria-label="Close upload" onClick={onClose} />
      )}
      <UploadArea
        onDragOver={event => event.preventDefault()}
        onDrop={event => {
          event.preventDefault();
          uploadFile(event.dataTransfer.files?.[0]);
        }}
      >
        <Icon name="image" size="large" />
        <label htmlFor={inputId}>{uploadMessage || 'Choose a cover image'}</label>
        <input
          id={inputId}
          aria-label="Cover image"
          type="file"
          accept="image/png,image/jpeg,image/jpg"
          disabled={uploading}
          onChange={event => {
            uploadFile(event.target.files?.[0]);
            event.target.value = '';
          }}
        />
        {uploading && <span role="status">Uploading…</span>}
      </UploadArea>
    </div>
  );
};

const mapStateToProps = state => ({
  user: state.user.user,
  idToken: state.user.idToken,
  currentBook: state.book.currentBook
});

const mapDispatchToProps = dispatch => bindActionCreators({
  changePage: newPage => push(newPage)
}, dispatch);

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(CoverFileUpload));
